import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IonRefresher,
  IonRefresherContent,
  IonAvatar,
  IonIcon,
  RefresherEventDetail,
} from '@ionic/react';
import { bookmarkOutline, starOutline, star } from 'ionicons/icons';
import { FileText, Users } from 'lucide-react';
import api from '../../lib/api';
import { Bill } from '../../types/bill';
import ProMobileBillCard from '../../components/pro/ProMobileBillCard';
import { ProEmptyState, ProLoadingList, ProSegmentTabs } from '../../components/pro/primitives';
import { getPositionColorClasses } from '../../utils/positionColors';
import { padDistrictNumber } from '../../utils/districtDisplay';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Workspace {
  id: string;
  name: string;
  description?: string;
  owner_org_name?: string;
}

interface TrackedBillItem {
  id: string;
  bill_id?: string;
  bill_number?: string;
  billNumber?: string;
  title?: string;
  status?: string;
  position?: string;
  billPosition?: string;
  is_hot?: boolean;
  sponsors?: Bill['sponsors'];
  committees?: Bill['committees'];
  primary_sponsor?: string;
}

interface TrackedLegislatorItem {
  id: string;
  legislator_id?: string;
  /** API returns camelCase; keep snake_case for older payloads. */
  full_name?: string;
  fullName?: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  party_affiliation?: string;
  partyAffiliation?: string;
  chamber?: string;
  district?: string;
  position?: string;
  is_priority?: boolean;
  isPriority?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const mapTrackedBillToModel = (item: TrackedBillItem): Bill => ({
  id: item.bill_id || item.id,
  billNumber: item.bill_number || item.billNumber || '',
  title: item.title || '',
  status: item.status || '',
  billPosition: item.position || item.billPosition,
  isHot: item.is_hot,
  session: '',
  subject: [],
  summary: '',
  location: '',
  lastActivity: '',
  sponsors: item.sponsors?.length
    ? item.sponsors
    : item.primary_sponsor
      ? [{
          name: item.primary_sponsor,
          title: '',
          type: 'primary',
          sponsor_type: 'primary',
          party: '',
        }]
      : [],
  committees: item.committees || [],
  categories: [],
});

const legislatorDisplayName = (leg: TrackedLegislatorItem): string => {
  const full = (leg.full_name || leg.fullName || '').trim();
  if (full) return full;
  return `${leg.first_name || leg.firstName || ''} ${leg.last_name || leg.lastName || ''}`.trim();
};

const getLegislatorInitials = (leg: TrackedLegislatorItem): string => {
  const full = legislatorDisplayName(leg);
  if (full) {
    const parts = full.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.[0]?.toUpperCase() || '?';
  }
  return '?';
};

const getLegislatorFullName = (leg: TrackedLegislatorItem): string => {
  return legislatorDisplayName(leg) || 'Unknown';
};

const getPartyColor = (party: string | undefined): string => {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) return 'bg-blue-600';
  if (p.includes('republican')) return 'bg-red-600';
  return 'bg-background-quaternary dark:bg-background-dark-tertiary';
};

// ---------------------------------------------------------------------------
// Legislator Row
// ---------------------------------------------------------------------------

interface LegislatorRowProps {
  item: TrackedLegislatorItem;
  onClick: (item: TrackedLegislatorItem) => void;
}

const LegislatorRow: React.FC<LegislatorRowProps> = ({ item, onClick }) => {
  const initials = getLegislatorInitials(item);
  const fullName = getLegislatorFullName(item);
  const partyColor = getPartyColor(item.party_affiliation || item.partyAffiliation);
  const position = item.position;

  const subtitle = [
    item.chamber,
    item.district ? `District ${padDistrictNumber(item.district)}` : undefined,
    item.party_affiliation,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      onClick={() => onClick(item)}
      className="w-full text-left flex items-center gap-3 px-0 py-3 border-b border-border-light dark:border-white/10 transition-opacity active:opacity-70"
    >
      {/* Avatar */}
      <IonAvatar
        className={`flex-shrink-0 flex items-center justify-center ${partyColor} rounded-full`}
        style={{ width: 44, height: 44 }}
      >
        <span className="text-white text-sm font-bold">{initials}</span>
      </IonAvatar>

      {/* Name + subtitle */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-text-primary dark:text-text-dark-primary truncate">
          {fullName}
        </p>
        {subtitle && (
          <p className="text-xs text-text-secondary dark:text-text-dark-secondary truncate mt-0.5">
            {subtitle}
          </p>
        )}
        {/* Position chip */}
        {position && (
          <span
            className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${getPositionColorClasses(position)}`}
          >
            {position}
          </span>
        )}
      </div>

      {/* Right-side indicators */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Priority star */}
        <IonIcon
          icon={item.is_priority ? star : starOutline}
          className={
            item.is_priority
              ? 'text-amber-500 dark:text-amber-400'
              : 'text-text-tertiary dark:text-white/20'
          }
          style={{ fontSize: 16 }}
        />
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

const ProWorkspaceDetailPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState<'bills' | 'legislators'>('bills');

  // ── Workspace query ────────────────────────────────────────────────────────

  const { data: workspace } = useQuery(
    ['pro-workspace', id],
    async () => {
      const response = await api.get(`/workspaces/${id}`);
      return response.data?.data || response.data;
    },
    { staleTime: 60000 },
  );

  // ── Tracked bills query ────────────────────────────────────────────────────

  const {
    data: billsData,
    isLoading: billsLoading,
    refetch: refetchBills,
  } = useQuery(
    ['pro-workspace-bills', id],
    async () => {
      const response = await api.get(`/workspace-bill-tracking/workspace/${id}`);
      const result = response.data?.data || response.data;
      const bills = Array.isArray(result) ? result : result?.bills || result?.items || [];
      return bills as TrackedBillItem[];
    },
    { staleTime: 30000 },
  );

  // ── Tracked legislators query ──────────────────────────────────────────────

  const {
    data: legislatorsData,
    isLoading: legislatorsLoading,
    refetch: refetchLegislators,
  } = useQuery(
    ['pro-workspace-legislators', id],
    async () => {
      const response = await api.get(`/workspace-legislator-tracking/workspace/${id}`);
      const result = response.data?.data || response.data;
      const legislators = Array.isArray(result) ? result : result?.legislators || [];
      return legislators as TrackedLegislatorItem[];
    },
    { staleTime: 30000 },
  );

  // ── Derived state ─────────────────────────────────────────────────────────

  const bills: TrackedBillItem[] = billsData || [];
  const legislators: TrackedLegislatorItem[] = legislatorsData || [];

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchBills(), refetchLegislators()]);
    e.detail.complete();
  };

  const handleBillClick = (bill: Bill) => {
    const identifier = bill.billNumber || bill.id;
    navigate(`/pro/bills/${identifier}`, {
      state: { trackingWorkspaceId: id },
    });
  };

  const handleLegislatorClick = (leg: TrackedLegislatorItem) => {
    const legId = leg.legislator_id || leg.id;
    navigate(`/pro/legislators/${legId}`, {
      state: { trackingWorkspaceId: id },
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          <h1 className="text-xl font-bold text-text-primary dark:text-text-dark-primary truncate">
            {(workspace as Workspace | undefined)?.name || '\u00A0'}
          </h1>
          {(workspace as Workspace | undefined)?.owner_org_name && (
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-0.5 truncate">
              {(workspace as Workspace).owner_org_name}
            </p>
          )}
        </div>

        {/* Segment toggle */}
        <div className="px-4 pb-3">
          <ProSegmentTabs
            value={activeSection}
            onChange={(v) => setActiveSection(v as 'bills' | 'legislators')}
            options={[
              {
                value: 'bills',
                label: (
                  <span className="flex items-center gap-1.5 text-sm">
                    <FileText size={14} />
                    {t('workspaceDetail.billTracking', 'Bill Tracking')}
                  </span>
                ),
              },
              {
                value: 'legislators',
                label: (
                  <span className="flex items-center gap-1.5 text-sm">
                    <Users size={14} />
                    {t('workspaceDetail.legislatorTracking', 'Legislator Tracking')}
                  </span>
                ),
              },
            ]}
            className="rounded-lg"
          />
        </div>

        {/* Section content */}
        <div className="px-4 pb-6">
          {/* ── Bills Section ────────────────────────────────────────────── */}
          {activeSection === 'bills' && (
            <>
              {billsLoading ? (
                <ProLoadingList variant="card" count={3} className="space-y-3" />
              ) : bills.length === 0 ? (
                <ProEmptyState
                  icon={bookmarkOutline}
                  message={t('workspaceDetail.emptyBills', 'No bills being tracked in this workspace')}
                  actionLabel={t('workspaceDetail.addBills', 'Browse Bills')}
                  onAction={() => navigate('/pro/bills')}
                />
              ) : (
                <div className="space-y-3">
                  {bills.map((item) => {
                    const mapped = mapTrackedBillToModel(item);
                    const position = item.position || item.billPosition;
                    return (
                      <ProMobileBillCard
                        key={item.id}
                        bill={mapped}
                        isHotBill={item.is_hot}
                        position={position}
                        onClick={handleBillClick}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Legislators Section ──────────────────────────────────────── */}
          {activeSection === 'legislators' && (
            <>
              {legislatorsLoading ? (
                <ProLoadingList variant="list-row" count={3} />
              ) : legislators.length === 0 ? (
                <ProEmptyState
                  icon={<Users size={48} />}
                  message={t('workspaceDetail.emptyLegislators', 'No legislators being tracked')}
                  actionLabel={t('workspaceDetail.addLegislators', 'Browse Legislators')}
                  onAction={() => navigate('/pro/legislators')}
                />
              ) : (
                <div>
                  {legislators.map((leg) => (
                    <LegislatorRow
                      key={leg.id}
                      item={leg}
                      onClick={handleLegislatorClick}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProWorkspaceDetailPage;
